import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Tabs,
  Tab,
} from "@mui/material";
import { Refresh as RefreshIcon, Send as SendIcon } from "@mui/icons-material";
import { useCsvData, type CsvData, type EmailStatus } from "./useCsvData";
import { bulkEmailApi, type EmailLog } from "@/apis/bulk-email";
import { useEffect, useState } from "react";

/**
 * CsvDataTable Props
 */
interface CsvDataTableProps {
  csvData: CsvData[];
  showValidation?: boolean;
  onRetryFailed?: (failedEmails: CsvData[]) => void;
  onRetryPending?: (pendingEmails: CsvData[]) => void;
  companyId?: number; // 실시간 로그 조회용
  enableRealTimeSync?: boolean; // 실시간 동기화 활성화
}

/**
 * CSV 데이터 상세 테이블
 * @description CSV 데이터를 페이징이 있는 테이블로 표시하는 컴포넌트입니다.
 * @author Max <sud665@ymjlab.com>
 * @version 2.0.0
 * @since 2025-01-15
 * @lastModified 2025-01-15
 */
export const CsvDataTable = ({ 
  csvData, 
  showValidation = true,
  onRetryFailed,
  onRetryPending,
  companyId,
  enableRealTimeSync = false
}: CsvDataTableProps) => {
  // 커스텀 훅으로 로직 분리
  const {
    page,
    rowsPerPage,
    paginatedData,
    stats,
    statusFilter,
    handleChangePage,
    handleChangeRowsPerPage,
    handleStatusFilterChange,
    validateAllCsvData,
    getFailedEmails,
    getPendingEmails,
  } = useCsvData(csvData);

  // 실시간 이메일 로그 상태
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  
  // 탭 상태 (방안 2용)
  const [activeTab, setActiveTab] = useState<'csv' | 'logs'>('csv');

  // 실시간 로그 동기화
  useEffect(() => {
    if (!enableRealTimeSync || !companyId) return;

    const fetchEmailLogs = async () => {
      try {
        setIsLoadingLogs(true);
        const logData = await bulkEmailApi.getRecentEmailLogs(companyId, 100);
        setEmailLogs(logData.logs);
      } catch (error) {
        console.error('이메일 로그 조회 실패:', error);
      } finally {
        setIsLoadingLogs(false);
      }
    };

    // 초기 로드
    fetchEmailLogs();

    // 5초마다 실시간 업데이트
    const interval = setInterval(fetchEmailLogs, 5000);

    return () => clearInterval(interval);
  }, [enableRealTimeSync, companyId]);

  // CSV 데이터와 이메일 로그 매칭
  const syncedCsvData = csvData.map(csvRow => {
    if (!enableRealTimeSync || !emailLogs.length) return csvRow;

    // 이메일로 매칭되는 로그 찾기
    const matchingLog = emailLogs.find(log => log.email === csvRow.email);
    
    if (matchingLog) {
      return {
        ...csvRow,
        emailStatus: matchingLog.sendStatus.toLowerCase() as EmailStatus,
        errorMessage: matchingLog.errorMessage || csvRow.errorMessage,
        sentAt: matchingLog.sentAt || csvRow.sentAt,
      };
    }

    return csvRow;
  });

  if (csvData.length === 0) return null;

  // CSV 유효성 검증 (옵션)
  const validationResult = showValidation ? validateAllCsvData(csvData) : { isValid: true, errors: [] };

  // 상태 라벨 헬퍼 함수
  const getStatusLabel = (status: EmailStatus | 'all') => {
    switch (status) {
      case 'pending': return '대기중';
      case 'sending': return '발송중';
      case 'success': return '성공';
      case 'failed': return '실패';
      case 'all': return '전체';
      default: return '전체';
    }
  };

  return (
    <Paper elevation={0} variant="outlined">
      <Box sx={{ p: 3 }}>
        {/* 헤더와 탭 */}
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6" fontWeight="bold">
              📊 데이터 및 로그 관리
              {enableRealTimeSync && (
                <Chip 
                  label={isLoadingLogs ? "동기화 중..." : "실시간 동기화"} 
                  color={isLoadingLogs ? "warning" : "success"} 
                  size="small" 
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
          </Box>
          
          {/* 탭 메뉴 */}
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab 
              label={`CSV 데이터 (${stats.totalCount}명)`} 
              value="csv" 
            />
            {enableRealTimeSync && (
              <Tab 
                label={`발송 로그 (${emailLogs.length}건)`} 
                value="logs" 
              />
            )}
          </Tabs>
        </Box>

        {/* 통계 (CSV 탭에서만 표시) */}
        {activeTab === 'csv' && (
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mb={2}
          >
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                label={`총 ${stats.totalCount}명`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`대기 ${stats.pendingCount}명`}
                color="default"
                variant="outlined"
              />
              <Chip
                label={`발송중 ${stats.sendingCount}명`}
                color="info"
                variant="outlined"
              />
              <Chip
                label={`성공 ${stats.successCount}명`}
                color="success"
                variant="outlined"
              />
              <Chip
                label={`실패 ${stats.failedCount}명`}
                color="error"
                variant="outlined"
              />
            </Box>
          </Box>
        )}

        {/* 필터링 및 액션 버튼 (CSV 탭에서만 표시) */}
        {activeTab === 'csv' && (
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>발송 상태 필터</InputLabel>
                <Select
                  value={statusFilter}
                  label="발송 상태 필터"
                  onChange={(e) => handleStatusFilterChange(e.target.value as EmailStatus | 'all')}
                >
                  <MenuItem value="all">전체 ({stats.totalCount}명)</MenuItem>
                  <MenuItem value="pending">대기중 ({stats.pendingCount}명)</MenuItem>
                  <MenuItem value="sending">발송중 ({stats.sendingCount}명)</MenuItem>
                  <MenuItem value="success">성공 ({stats.successCount}명)</MenuItem>
                  <MenuItem value="failed">실패 ({stats.failedCount}명)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <Box display="flex" gap={1} justifyContent="flex-end">
                {stats.failedCount > 0 && onRetryFailed && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<RefreshIcon />}
                    onClick={() => onRetryFailed(getFailedEmails())}
                    size="small"
                  >
                    실패 재전송 ({stats.failedCount}명)
                  </Button>
                )}
                {stats.pendingCount > 0 && onRetryPending && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SendIcon />}
                    onClick={() => onRetryPending(getPendingEmails())}
                    size="small"
                  >
                    대기 발송 ({stats.pendingCount}명)
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        )}

        {/* CSV 검증 오류 표시 (CSV 탭에서만 표시) */}
        {activeTab === 'csv' && showValidation && !validationResult.isValid && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
              CSV 파일에 오류가 있습니다:
            </Typography>
            {validationResult.errors.slice(0, 5).map((error, index) => (
              <Typography key={index} variant="body2">
                • {error}
              </Typography>
            ))}
            {validationResult.errors.length > 5 && (
              <Typography variant="body2" color="text.secondary" mt={1}>
                ... 및 {validationResult.errors.length - 5}개 추가 오류
              </Typography>
            )}
          </Alert>
        )}

        {/* CSV 형식 안내 (CSV 탭에서만 표시) */}
        {activeTab === 'csv' && (
          <Box sx={{ mb: 2, p: 2, bgcolor: validationResult.isValid ? "grey.50" : "error.50", borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              💡 <strong>CSV 형식 안내:</strong> Email, First name, Last name, Phone 컬럼이 필요합니다.
              <br />
              <strong>필수:</strong> Email만 &nbsp;&nbsp;
              <strong>선택:</strong> First name, Last name, Phone (모두 선택사항)
              <br />
              🔄 <strong>자동 처리:</strong> 중복된 이메일은 자동으로 제거됩니다.
              <br />
              ✅ <strong>허용 사항:</strong> First name이 없으면 "Unknown"으로 자동 설정됩니다.
              <br />
              예시: john.doe@example.com, John, Doe, 010-1234-5678
            </Typography>
          </Box>
        )}

        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                {activeTab === 'csv' ? (
                  <>
                    <TableCell sx={{ fontWeight: "bold" }}>번호</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>이메일</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>이름</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>성</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>전화번호</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>발송상태</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>발송시간</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>오류메시지</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: "bold" }}>발송시간</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>이메일</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>수신자</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>상태</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>서비스</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>제목</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>오류</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {activeTab === 'csv' ? (
                (enableRealTimeSync ? syncedCsvData.slice(page * rowsPerPage, (page + 1) * rowsPerPage) : paginatedData).map((row, index) => {
                const rowNumber = page * rowsPerPage + index + 1;
                const hasError = !row.email;
                const status = row.emailStatus || 'pending';
                
                // 상태별 색상 결정
                const getStatusColor = (status: EmailStatus) => {
                  switch (status) {
                    case 'pending': return 'default';
                    case 'sending': return 'info';
                    case 'success': return 'success';
                    case 'failed': return 'error';
                    default: return 'default';
                  }
                };



                return (
                  <TableRow 
                    key={index} 
                    hover 
                    sx={{ 
                      backgroundColor: hasError ? "error.50" : "transparent",
                      "&:hover": {
                        backgroundColor: hasError ? "error.100" : "action.hover"
                      }
                    }}
                  >
                    <TableCell>{rowNumber}</TableCell>
                    <TableCell 
                      sx={{ 
                        color: !row.email ? "error.main" : "inherit",
                        fontWeight: !row.email ? "bold" : "normal"
                      }}
                    >
                      {row.email || "❌ 필수"}
                    </TableCell>
                    <TableCell>
                      {row.firstName || "Unknown"}
                    </TableCell>
                    <TableCell>{row.lastName || "-"}</TableCell>
                    <TableCell>{row.phone || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        label={hasError ? "오류" : getStatusLabel(status)}
                        color={hasError ? "error" : getStatusColor(status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {row.sentAt ? new Date(row.sentAt).toLocaleString('ko-KR', {
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : "-"}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        color: row.errorMessage ? "error.main" : "inherit",
                        fontSize: "0.75rem",
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                      title={row.errorMessage || ""}
                    >
                      {row.errorMessage || "-"}
                    </TableCell>
                  </TableRow>
                );
              })
              ) : (
                // 로그 탭 내용
                emailLogs.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((log) => (
                  <TableRow key={log.logId} hover>
                    <TableCell>
                      {new Date(log.regDttm).toLocaleString('ko-KR', {
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {log.email}
                    </TableCell>
                    <TableCell>{log.recipientName}</TableCell>
                    <TableCell>
                      <Chip
                        label={log.sendStatus === 'SUCCESS' ? '성공' : log.sendStatus === 'FAILED' ? '실패' : '대기중'}
                        color={log.sendStatus === 'SUCCESS' ? 'success' : log.sendStatus === 'FAILED' ? 'error' : 'warning'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={log.emailService} size="small" variant="filled" />
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={log.subject}
                    >
                      {log.subject}
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 150,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: log.errorMessage ? 'error.main' : 'inherit',
                      }}
                      title={log.errorMessage || ''}
                    >
                      {log.errorMessage || '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={activeTab === 'csv' ? stats.filteredCount : emailLogs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="페이지당 행 수:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} / ${statusFilter === 'all' ? '전체' : getStatusLabel(statusFilter)} ${count}개`
          }
        />
      </Box>
    </Paper>
  );
};
